import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { Package } from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { superAdminService } from '@/api/super-admin'
import type { Plan, PlanListParams } from '@/types/plan'
import { Plus, MoreHorizontal, Pencil, Trash2, Coins } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/table'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import { useTranslation as useI18n } from 'react-i18next'
import { DataTableFacetedFilter } from '@/views/users/components/data-table-faceted-filter'
import { debounceAsync } from '@/utils/debounce'
import { timestamp_to_date } from '@/utils/time'

export default function PlansManagement() {
  const { t } = useTranslation()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagesCnt, setPagesCnt] = useState(0)
  const [newExtraKey, setNewExtraKey] = useState('')
  const [newExtraValue, setNewExtraValue] = useState('')

  const [params, setParams] = useState<PlanListParams>({
    ordering: null,
    search: null,
    page_size: 10,
    page: 0,
    is_active: ['true'],
    is_public: null,
  })

  const [isCreateOpen, setCreateOpen] = useState(false)
  const [isEditOpen, setEditOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)
  const [form, setForm] = useState<Plan>({
    code: '',
    name: '',
    description: '',
    max_users: 0,
    monthly_token_cap: null,
    audio_access_enabled: null,
    max_storage_mb: null,
    max_cases: null,
    max_active_cases: null,
    extras: {},
    price_monthly_cents: null,
    price_yearly_cents: null,
    trial_days: null,
    is_active: true,
    is_public: true,
    is_recommended: false,
  })

  const loadPlans = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await superAdminService.getPlans(params)
      setPlans(data.results || [])
      setPagesCnt(Math.ceil((data.count || 0) / (params.page_size || 10)))
    } catch (e: any) {
      setError(e?.message || 'Failed to load plans')
      setPlans([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.ordering, params.search, params.page, params.page_size, params.is_active, params.is_public])

  const resetForm = () => {
    setForm({
      code: '',
      name: '',
      description: '',
      max_users: 0,
      monthly_token_cap: null,
      audio_access_enabled: null,
      max_storage_mb: null,
      max_cases: null,
      max_active_cases: null,
      extras: {},
      price_monthly_cents: null,
      price_yearly_cents: null,
      trial_days: null,
      is_active: true,
      is_public: true,
      is_recommended: false,
    })
  }

  const onCreateOpen = () => {
    resetForm()
    setEditingPlan(null)
    setCreateOpen(true)
  }

  const handleCreate = async () => {
    if (!form.code || !form.name) return
    try {
      const created = await superAdminService.createPlan(form)
      setPlans((prev) => [created, ...prev])
      setCreateOpen(false)
    } catch (e: any) {
      alert(e?.response?.data?.detail || e?.message || 'Failed to create plan')
    }
  }

  const onEditOpen = (plan: Plan) => {
    setEditingPlan(plan)
    setForm({
      code: plan.code,
      name: plan.name,
      description: plan.description || '',
      max_users: plan.max_users ?? 0,
      monthly_token_cap: plan.monthly_token_cap ?? null,
      audio_access_enabled: plan.audio_access_enabled ?? null,
      max_storage_mb: plan.max_storage_mb ?? null,
      max_cases: plan.max_cases ?? null,
      max_active_cases: plan.max_active_cases ?? null,
      extras: plan.extras || {},
      price_monthly_cents: plan.price_monthly_cents ?? null,
      price_yearly_cents: plan.price_yearly_cents ?? null,
      trial_days: plan.trial_days ?? null,
      is_active: !!plan.is_active,
      is_public: !!plan.is_public,
      is_recommended: !!plan.is_recommended,
      id: plan.id,
      created_at: plan.created_at,
    })
    setEditOpen(true)
  }

  const handleUpdate = async () => {
    if (!editingPlan?.id) return
    try {
      const updated = await superAdminService.patchPlan(String(editingPlan.id), form)
      setPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
      setEditOpen(false)
    } catch (e: any) {
      alert(e?.response?.data?.detail || e?.message || 'Failed to update plan')
    }
  }

  const handleDelete = async (plan: Plan) => {
    if (!plan.id) return
    const confirmed = confirm(`Delete plan "${plan.name}"?`)
    if (!confirmed) return
    try {
      await superAdminService.deletePlan(String(plan.id))
      setPlans((prev) => prev.filter((p) => p.id !== plan.id))
    } catch (e: any) {
      alert(e?.response?.data?.detail || e?.message || 'Failed to delete plan')
    }
  }

  const priceDisplay = (cents?: number | null) => {
    if (cents == null) return '-'
    return `${(cents / 100).toFixed(2)} MAD/mo`
  }

  // Columns for DataTable
  const columns: ColumnDef<Plan>[] = useMemo(() => [
    {
      accessorKey: 'code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('code') || 'Code'} />
      ),
      cell: ({ row }) => <div className='font-medium'>{row.original.code}</div>,
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('name') || 'Name'} />
      ),
      cell: ({ row }) => <div>{row.original.name}</div>,
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('description') || 'Description'} />
      ),
      cell: ({ row }) => (
        <div className='text-muted-foreground truncate max-w-[240px]'>
          {row.original.description || '-'}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'price',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('price') || 'Price'} />
      ),
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Coins className='h-4 w-4 text-amber-600' />
          {priceDisplay(row.original.price_monthly_cents)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'is_active',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status') || 'Status'} />
      ),
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'default' : 'secondary'} className={row.original.is_active ? 'bg-green-600' : 'bg-slate-300'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'is_public',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('public') || 'Public'} />
      ),
      cell: ({ row }) => (
        row.original.is_public ? (
          <Badge variant='outline' className='text-green-700 border-green-300'>Public</Badge>
        ) : (
          <Badge variant='outline' className='text-slate-700 border-slate-300'>Private</Badge>
        )
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('created_at') || 'Created'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap'>
          {row.original.created_at ? timestamp_to_date(Number(row.original.created_at)).toLocaleDateString() : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={() => onEditOpen(row.original)}>
              <Pencil className='mr-2 h-4 w-4' />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDelete(row.original)} className='text-red-600'>
              <Trash2 className='mr-2 h-4 w-4' />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ], [t])

  // Toolbar component with search and filters
  function PlansTableToolbar({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    table, params, setParams
  }: { table: any, params: PlanListParams, setParams: React.Dispatch<React.SetStateAction<PlanListParams>> }) {
    const i18n = useI18n()

    const search = debounceAsync(async (value: string) => {
      setParams({ ...params, search: value === '' ? null : value })
    }, 500)

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      search(event.target.value)
    }

    const handleActiveFacetChange = (values: string[]) => {
      setParams({ ...params, is_active: values.length <= 1 ? values : null })
    }

    const handlePublicFacetChange = (values: string[]) => {
      setParams({ ...params, is_public: values.length <= 1 ? values : null })
    }

    return (
      <div className='flex items-center justify-between'>
        <div className='flex flex-1 flex-col-reverse items-start gap-y-2 gap-x-2 sm:flex-row sm:items-center sm:space-x-2'>
          <Input
            defaultValue={params.search || ''}
            placeholder={i18n.t('search')}
            onChange={handleSearchChange}
            className='h-8 w-[150px] lg:w-[250px]'
          />
          <div className='flex gap-x-2'>
            {table.getColumn('is_active') && (
              <DataTableFacetedFilter
                title={i18n.t('status')}
                options={[
                  { label: i18n.t('status_active'), value: 'true' },
                  { label: i18n.t('status_locked'), value: 'false' },
                ]}
                defaultSelected={params.is_active || []}
                onFacetChange={handleActiveFacetChange}
              />
            )}
            {table.getColumn('is_public') && (
              <DataTableFacetedFilter
                title={i18n.t('visibility')}
                options={[
                  { label: 'Public', value: 'true' },
                  { label: 'Private', value: 'false' },
                ]}
                defaultSelected={params.is_public || []}
                onFacetChange={handlePublicFacetChange}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderFormFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="code">Code</Label>
        <Input id="code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="basic, pro, enterprise" />
      </div>
      <div>
        <Label htmlFor="name">Name</Label>
        <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Basic Plan" />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="description">Description</Label>
        <Input id="description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" />
      </div>
      <div>
        <Label htmlFor="price">Monthly Price (MAD)</Label>
        <Input id="price" type="number" min={0} step={0.01} value={
          form.price_monthly_cents == null ? '' : form.price_monthly_cents / 100
        } onChange={(e) => {
          const value = e.target.value
          setForm({
            ...form,
            price_monthly_cents: value === '' ? null : Math.round(Number(value) * 100)
          })
        }} />
      </div>
      <div>
        <Label htmlFor="price_yearly">Yearly Price (MAD)</Label>
        <Input id="price_yearly" type="number" min={0} step={0.01} value={
          form.price_yearly_cents == null ? '' : form.price_yearly_cents / 100
        } onChange={(e) => {
          const value = e.target.value
          setForm({
            ...form,
            price_yearly_cents: value === '' ? null : Math.round(Number(value) * 100)
          })
        }} />
      </div>
      <div>
        <Label htmlFor="max_users">Max Users</Label>
        <Input id="max_users" type="number" min={0} value={form.max_users ?? 0} onChange={(e) => setForm({ ...form, max_users: Number(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="monthly_token_cap">Monthly Token Cap</Label>
        <Input id="monthly_token_cap" type="number" min={0} value={form.monthly_token_cap ?? ''} onChange={(e) => setForm({ ...form, monthly_token_cap: e.target.value === '' ? null : Number(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="audio_access_enabled">Audio Access Enabled</Label>
        <Input id="audio_access_enabled" type="number" min={0} value={form.audio_access_enabled ?? ''} onChange={(e) => setForm({ ...form, audio_access_enabled: e.target.value === '' ? null : Number(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="max_storage_mb">Max Storage (MB)</Label>
        <Input id="max_storage_mb" type="number" min={0} value={form.max_storage_mb ?? ''} onChange={(e) => setForm({ ...form, max_storage_mb: e.target.value === '' ? null : Number(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="max_cases">Max Cases</Label>
        <Input id="max_cases" type="number" min={0} value={form.max_cases ?? ''} onChange={(e) => setForm({ ...form, max_cases: e.target.value === '' ? null : Number(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="max_active_cases">Max Active Cases</Label>
        <Input id="max_active_cases" type="number" min={0} value={form.max_active_cases ?? ''} onChange={(e) => setForm({ ...form, max_active_cases: e.target.value === '' ? null : Number(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="trial_days">Trial Days</Label>
        <Input id="trial_days" type="number" min={0} value={form.trial_days ?? ''} onChange={(e) => setForm({ ...form, trial_days: e.target.value === '' ? null : Number(e.target.value) })} />
      </div>
      <div className="md:col-span-2">
        <Label>Extras</Label>
        <div className="space-y-2 mt-2">
          {(Object.entries(form.extras || {}).length === 0) && (
            <div className='text-sm text-muted-foreground'>No extras</div>
          )}
          {Object.entries(form.extras || {}).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2">
              <Input value={key} readOnly className="w-44" />
              <Input value={`${value ?? ''}`} onChange={(e) => setForm({ ...form, extras: { ...(form.extras || {}), [key]: e.target.value } })} />
              <Button variant="outline" onClick={() => {
                const { [key]: _removed, ...rest } = (form.extras || {}) as Record<string, unknown>
                setForm({ ...form, extras: rest })
              }}>Remove</Button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Input placeholder="key" value={newExtraKey} onChange={(e) => setNewExtraKey(e.target.value)} className="w-44" />
            <Input placeholder="value" value={newExtraValue} onChange={(e) => setNewExtraValue(e.target.value)} />
            <Button onClick={() => {
              if (!newExtraKey) return
              setForm({ ...form, extras: { ...(form.extras || {}), [newExtraKey]: newExtraValue } })
              setNewExtraKey('')
              setNewExtraValue('')
            }}>Add</Button>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="is_active" checked={!!form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: !!checked })} />
        <Label htmlFor="is_active">Active</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="is_public" checked={!!form.is_public} onCheckedChange={(checked) => setForm({ ...form, is_public: !!checked })} />
        <Label htmlFor="is_public">Public</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="is_recommended" checked={!!form.is_recommended} onCheckedChange={(checked) => setForm({ ...form, is_recommended: !!checked })} />
        <Label htmlFor="is_recommended">Recommended</Label>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">
        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Package className="h-5 w-5" />
                </span>
              }
              title="Plans"
              description="Manage subscription plans for the platform."
              actions={
                <Button onClick={onCreateOpen}>
                  <Plus className="h-4 w-4 me-2" />
                  Create Plan
                </Button>
              }
            />

            <Card>
              <CardContent className="p-4 space-y-4">
                {loading ? (
                  <div className='py-8 text-center'>Loading...</div>
                ) : error ? (
                  <div className='py-8 text-center text-red-600'>{error}</div>
                ) : (
                  <DataTable
                    data={plans}
                    columns={columns}
                    params={params}
                    setParams={setParams}
                    pages={pagesCnt}
                    ToolbarName={PlansTableToolbar}
                    storageKey="super-admin-plans-table"
                  />
                )}
              </CardContent>
            </Card>

            {/* Create Plan Dialog */}
            <Dialog open={isCreateOpen} onOpenChange={setCreateOpen}>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Create Plan</DialogTitle>
                </DialogHeader>
                {renderFormFields()}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreate} disabled={!form.code || !form.name}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Plan Dialog */}
            <Dialog open={isEditOpen} onOpenChange={setEditOpen}>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Edit Plan</DialogTitle>
                </DialogHeader>
                {renderFormFields()}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button onClick={handleUpdate} disabled={!form.code || !form.name}>Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </Main>
      </div>
    </div>
  )
}